/**
 * Constantes de routes de l'application Luxori SaaS
 * Centralisation de toutes les routes avec typage fort
 */

export const ROUTES = {
  // Routes publiques
  LOGIN: '/login',

  // Routes principales
  HOME: '/',
  DASHBOARD: '/dashboard',

  // Routes métier
  CLIENTS: '/clients',
  FOURNISSEURS: '/fournisseurs',
  ARTICLES: '/articles',
  PAYMENT_METHODS: '/payment-methods',
  VENDORS: '/vendors',
  DELIVERIES: {
    BASE: '/deliveries',
    prepareOrder: (id: string) => `/deliveries/preparation/${id}`,
  },
  DELIVERY_TIMINGS: '/delivery-timings',
  SERVICE_TYPES: '/service-types',
  LIFT_TYPES: '/lift-types',
  SEARCH: '/recherche',
  DAILY_SALES: '/ca-du-jour',
  PERIOD_STATISTICS: '/statistiques-periode',
  PAYMENT_DELETION_AUDIT: '/surveillance-acomptes',
  MARKETING_STUDIO: '/marketing-studio',
  MARKETING_STUDIO_NEW: '/marketing-studio/nouveau',
  MARKETING_STUDIO_EDIT: (id: string) => `/marketing-studio/${id}`,
  CONTACT_MESSAGES: '/messages-contact',
  SAV_REQUESTS: '/demandes-sav-site',
  PROMOTIONS_SITE: '/promotions-site',

  // Routes des services après-vente
  AFTER_SALES: {
    BASE: '/business/services-apres-vente',
    NEW: '/business/services-apres-vente/nouveau',
    EDIT: '/business/services-apres-vente/edit',
    VIEW: '/business/services-apres-vente/view',

    // Fonctions helpers pour les routes dynamiques
    editAfterSales: (id: string) => `/business/services-apres-vente/edit/${id}`,
    viewAfterSales: (id: string) => `/business/services-apres-vente/view/${id}`,
  },

  // Routes des devis
  QUOTES: {
    BASE: '/devis',
    NEW: '/devis/nouveau',
    EDIT: '/devis/edit',
    VIEW: '/devis/view',

    // Fonctions helpers pour les routes dynamiques
    editQuote: (id: string) => `/devis/${id}/modifier`,
    viewQuote: (id: string) => `/devis/view/${id}`,
  },

  // Achats (factures fournisseurs)
  ACHATS: '/business/achats',

  // Paiements fournisseurs
  PURCHASE_PAYMENTS: '/business/paiements-fournisseurs',

  // Factures à payer
  FACTURES_A_PAYER: '/business/factures-a-payer',

  // Commandes fournisseurs
  SUPPLIER_ORDERS: '/business/commandes-fournisseurs',
  PENDING_SUPPLIER_ORDERS: '/business/commandes-en-attente',

  // Inventaire
  INVENTAIRE: '/inventaire',
  INVENTAIRE_2023: '/inventaire-2023',

  // Routes des bons de commande
  ORDERS: {
    BASE: '/business/bons-de-commande',
    NEW: '/business/bons-de-commande/nouveau',
    EDIT: '/business/bons-de-commande/edit',
    VIEW: '/business/bons-de-commande/view',

    // Fonctions helpers pour les routes dynamiques
    editOrder: (id: string) => `/business/bons-de-commande/edit/${id}`,
    viewOrder: (id: string) => `/business/bons-de-commande/view/${id}`,
  },

  // Routes des bons de commande version 2 (duplicata)
  ORDERS2: {
    BASE: '/business/bons-de-commande2',
    NEW: '/business/bons-de-commande2/nouveau',
    EDIT: '/business/bons-de-commande2/edit',
    VIEW: '/business/bons-de-commande2/view',

    // Fonctions helpers pour les routes dynamiques
    editOrder: (id: string) => `/business/bons-de-commande2/edit/${id}`,
    viewOrder: (id: string) => `/business/bons-de-commande2/view/${id}`,
  },

  // Routes des notes de crédit
  CREDIT_NOTES: {
    BASE: '/business/notes-de-credit',
    NEW: '/business/notes-de-credit/nouvelle',
    EDIT: '/business/notes-de-credit/edit',
    VIEW: '/business/notes-de-credit/view',

    // Fonctions helpers pour les routes dynamiques
    editCreditNote: (id: string) => `/business/notes-de-credit/edit/${id}`,
    viewCreditNote: (id: string) => `/business/notes-de-credit/view/${id}`,
  },

  // Routes d'administration
  SETTINGS: {
    ROOT: '/settings',
    OVERVIEW: '/settings/overview',
    BUSINESS: {
      PAYMENT_METHODS: '/settings/business/payment-methods',
      DELIVERY_TIMINGS: '/settings/business/delivery-timings',
      SERVICE_TYPES: '/settings/business/service-types',
      LIFT_TYPES: '/settings/business/lift-types',
      VENDORS: '/settings/business/vendors',
      STORES: '/settings/business/stores',
    },
    DOCUMENTS: {
      TERMS: '/settings/documents/terms',
    },
    ACCESS: {
      USERS: '/settings/access/users',
      PERMISSIONS: '/settings/access/permissions',
      DEVICES: '/settings/access/devices',
      CONTACT_SAV: '/settings/access/contact-sav',
      INBOUND_UNMATCHED: '/settings/access/courriers-a-classer',
      SAV_REMINDERS: '/settings/access/rappels-sav',
    },
    PERSONALIZATION: {
      HOME_LINKS: '/settings/personalization/home-links',
      DASHBOARD_BUTTONS: '/settings/personalization/dashboard-buttons',
      BUTTON_COLORS: '/settings/personalization/button-colors',
    },
    AI_ASSISTANT: '/settings/ai-assistant',
    BILLIT_TEST: '/settings/billit-test',
    GMAIL_TEST: '/settings/gmail-test',
    PURCHASE_INVOICES: '/settings/billit/purchase-invoices',
    FINALIZED_PURCHASE_INVOICES: '/settings/purchase-invoices-list',
    DUPLICATE_CLEANUP: '/settings/billit/duplicate-cleanup',
    BC2023: '/settings/billit/bc2023',
    VC2023: '/settings/billit/vc2023',
    INVENTORY_2023: '/settings/billit/inventory-2023',
    INVENTORY_2023_DUPLICATE_CLEANUP: '/settings/billit/inventory-2023-duplicate-cleanup',
    INVENTORY: '/settings/inventory',
    PURCHASE_INVOICES_LOGS: '/settings/billit/purchase-invoices/logs',
    CAISSE: '/settings/caisse',
  },
  DEVICE_AUTHORIZATION: '/device-authorization',

  // Routes factures
  INVOICES: {
    LIST: '/business/factures',
    NEW: '/business/factures/nouvelle',
    editInvoice: (id: string) => `/business/factures/${id}`,
  },

  // Routes factures d'acompte
  ACCOMPTE_INVOICES: {
    LIST: '/business/factures-acomptes',
  },

  // Routes de redirection (anciennes URLs)
  LEGACY: {
    NEW_ORDER: '/business/bons-de-commande/nouvelle-commande',
    EDIT_ORDER: '/business/bons-de-commande/modifier',
    VIEW_ORDER: '/business/bons-de-commande/voir',

    // Fonctions helpers pour les redirections
    editOrderOld: (id: string) => `/business/bons-de-commande/modifier/${id}`,
    viewOrderOld: (id: string) => `/business/bons-de-commande/voir/${id}`,
  },
} as const;

/**
 * Routes relatives (pour React Router nested routes)
 */
export const RELATIVE_ROUTES = {
  DASHBOARD: 'dashboard',
  MARKETING_STUDIO: 'marketing-studio',
  MARKETING_STUDIO_NEW: 'marketing-studio/nouveau',
  CONTACT_MESSAGES: 'messages-contact',
  SAV_REQUESTS: 'demandes-sav-site',
  PROMOTIONS_SITE: 'promotions-site',
  CLIENTS: 'clients',
  FOURNISSEURS: 'fournisseurs',
  ARTICLES: 'articles',
  DELIVERIES: {
    BASE: 'deliveries',
  },
  SETTINGS: 'settings',
  ACHATS: 'business/achats',
  PURCHASE_PAYMENTS: 'business/paiements-fournisseurs',
  FACTURES_A_PAYER: 'business/factures-a-payer',
  SUPPLIER_ORDERS: 'business/commandes-fournisseurs',
  PENDING_SUPPLIER_ORDERS: 'business/commandes-en-attente',
  INVENTAIRE: 'inventaire',
  INVENTAIRE_2023: 'inventaire-2023',
  ORDERS: {
    BASE: 'business/bons-de-commande',
    NEW: 'business/bons-de-commande/nouveau',
    NEW_LEGACY: 'business/bons-de-commande/nouvelle-commande',
  },
  ORDERS2: {
    BASE: 'business/bons-de-commande2',
    NEW: 'business/bons-de-commande2/nouveau',
  },
  CREDIT_NOTES: {
    BASE: 'business/notes-de-credit',
    NEW: 'business/notes-de-credit/nouvelle',
  },
} as const;

/**
 * Type pour les routes statiques
 */
export type RouteKeys = typeof ROUTES;

/**
 * Chemins de routes pour React Router (avec paramètres)
 */
export const ROUTE_PATTERNS = {
  ORDERS: {
    EDIT_WITH_ID: '/business/bons-de-commande/edit/:id',
    VIEW_WITH_ID: '/business/bons-de-commande/view/:id',
  },
  ORDERS2: {
    EDIT_WITH_ID: '/business/bons-de-commande2/edit/:id',
    VIEW_WITH_ID: '/business/bons-de-commande2/view/:id',
  },
  CREDIT_NOTES: {
    EDIT_WITH_ID: '/business/notes-de-credit/edit/:id',
    VIEW_WITH_ID: '/business/notes-de-credit/view/:id',
  },
  AFTER_SALES: {
    EDIT_WITH_ID: '/business/services-apres-vente/edit/:id',
    VIEW_WITH_ID: '/business/services-apres-vente/view/:id',
  },
  INVOICES: {
    EDIT_WITH_ID: '/business/factures/:id',
  },
  LEGACY: {
    EDIT_WITH_ID: '/business/bons-de-commande/modifier/:id',
    VIEW_WITH_ID: '/business/bons-de-commande/voir/:id',
  },
  DELIVERIES: {
    PREPARE_WITH_ID: 'deliveries/preparation/:orderId',
  },
  MARKETING_STUDIO: {
    EDIT_WITH_ID: '/marketing-studio/:id',
  },
} as const;
